import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { recruitmentCompanyVacancyService } from '../../services/recruitmentCompanyVacancyService';
import { authService } from '../../services/authService';
import { toast } from 'react-toastify';
import RecruitmentCompanyNav from '../../components/RecruitmentCompanyNav';
import RecruitmentCompanyBottomNavigation from '../../components/RecruitmentCompanyBottomNavigation';
import api from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation';

const RecruitmentCompanyCreateVacancy = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const { t, locale } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [jobTitles, setJobTitles] = useState([]);
  const [skills, setSkills] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm();

  const forCompanyName = watch('for_company_name');
  const forCompanyId = watch('for_company_id');

  useEffect(() => {
    loadJobTitles();
    loadSkills();
  }, []);

  const loadJobTitles = async () => {
    try {
      const response = await api.get('/job-titles');
      if (Array.isArray(response.data)) {
        setJobTitles(response.data);
      } else if (response.data?.data) {
        setJobTitles(response.data.data);
      }
    } catch (error) {
      console.error('Error loading job titles:', error);
    }
  };

  const loadSkills = async () => {
    try {
      const response = await api.get('/skills');
      if (Array.isArray(response.data)) {
        setSkills(response.data);
      } else if (response.data?.data) {
        setSkills(response.data.data);
      }
    } catch (error) {
      console.error('Error loading skills:', error);
    }
  };

  const handleSkillToggle = (skillId) => {
    setSelectedSkills(prev => {
      if (prev.includes(skillId)) {
        return prev.filter(id => id !== skillId);
      } else {
        return [...prev, skillId];
      }
    });
  };

  const handleForCompanyNameChange = (e) => {
    const value = e.target.value;
    setValue('for_company_name', value);
    // Clear company ID if name is entered
    if (value) {
      setValue('for_company_id', '');
    }
  };

  const handleForCompanyIdChange = (e) => {
    const value = e.target.value;
    setValue('for_company_id', value);
    // Clear company name if ID is selected
    if (value) {
      setValue('for_company_name', '');
    }
  };

  const onSubmit = async (data) => {
    try {
      setSubmitting(true);

      // Convert location to proper format
      if (data.location_lat && data.location_long) {
        data.location = {
          lat: parseFloat(data.location_lat),
          long: parseFloat(data.location_long),
        };
        delete data.location_lat;
        delete data.location_long;
      }

      // Add selected skills
      if (selectedSkills.length > 0) {
        data.skills = selectedSkills;
      }

      // Clean up company fields - send only one
      if (data.for_company_id) {
        data.for_company_id = parseInt(data.for_company_id);
        delete data.for_company_name;
      } else if (data.for_company_name) {
        delete data.for_company_id;
      } else {
        delete data.for_company_id;
        delete data.for_company_name;
      }

      const response = await recruitmentCompanyVacancyService.create(data);
      if (response.success) {
        toast.success(t('company.createVacancy.createSuccess') || 'تم إنشاء الوظيفة بنجاح');
        navigate('/recruitment-company/vacancies');
      }
    } catch (error) {
      const message = error.response?.data?.message || t('company.createVacancy.createFailed') || 'فشل إنشاء الوظيفة';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="employee-home-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <RecruitmentCompanyNav user={user} />

      {/* Header Section */}
      <div className="px-3 mb-4 mt-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h3 className="section-title mb-1">{t('company.createVacancy.title') || 'إنشاء وظيفة جديدة'}</h3>
            <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>{t('company.createVacancy.subtitle') || 'أضف تفاصيل الوظيفة'}</p>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="apply-form px-3 mb-5">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Company Information (for recruitment companies) */}
          <div className="form-section-apply mb-4">
            <h3 className="form-section-title">معلومات الشركة</h3>

            <div className="edit-profile-field-container">
              <label className="edit-profile-label" htmlFor="for_company_name">
                اسم الشركة <span style={{ color: '#608fd3' }}>*</span>
              </label>
              <input
                type="text"
                id="for_company_name"
                className="edit-profile-input"
                placeholder="أدخل اسم الشركة التي تعلن عن الوظيفة"
                disabled={!!forCompanyId}
                {...register('for_company_name', {
                  validate: (value) => {
                    if (!forCompanyId && !value) {
                      return 'يجب إدخال اسم الشركة';
                    }
                    return true;
                  }
                })}
                onChange={handleForCompanyNameChange}
              />
              {errors.for_company_name && (
                <span className="edit-profile-error">{errors.for_company_name.message}</span>
              )}
            </div>
          </div>

          {/* Basic Information */}
          <div className="form-section-apply mb-4">
            <h3 className="form-section-title">{t('company.createVacancy.basicInfo') || 'المعلومات الأساسية'}</h3>

            <div className="edit-profile-field-container">
              <label className="edit-profile-label" htmlFor="job_title_id">
                {t('company.createVacancy.jobTitle') || 'المسمى الوظيفي'} <span style={{ color: '#608fd3' }}>*</span>
              </label>
              <select
                id="job_title_id"
                className="edit-profile-input"
                {...register('job_title_id', {
                  required: t('company.createVacancy.jobTitleRequired') || 'المسمى الوظيفي مطلوب',
                })}
              >
                <option value="">{t('company.createVacancy.selectJobTitle') || 'اختر المسمى الوظيفي'}</option>
                {jobTitles.map((jobTitle) => (
                  <option key={jobTitle.id} value={jobTitle.id}>
                    {jobTitle.name}
                  </option>
                ))}
              </select>
              {errors.job_title_id && (
                <span className="edit-profile-error">{errors.job_title_id.message}</span>
              )}
            </div>

            <div className="edit-profile-field-container">
              <label className="edit-profile-label" htmlFor="type_of_work">{t('company.createVacancy.workType') || 'نوع العمل'}</label>
              <select id="type_of_work" className="edit-profile-input" {...register('type_of_work')}>
                <option value="">{t('company.createVacancy.selectType') || 'اختر النوع'}</option>
                <option value="full_time">{t('employee.home.workType.fullTime') || 'دوام كامل'}</option>
                <option value="part_time">{t('employee.home.workType.partTime') || 'دوام جزئي'}</option>
              </select>
            </div>

            <div className="edit-profile-field-container">
              <label className="edit-profile-label" htmlFor="presence_type">{t('company.createVacancy.presenceType') || 'نوع الحضور'}</label>
              <select id="presence_type" className="edit-profile-input" {...register('presence_type')}>
                <option value="">{t('company.createVacancy.selectType') || 'اختر النوع'}</option>
                <option value="remote">{t('employee.home.presenceType.remote') || 'عن بعد'}</option>
                <option value="hybrid">{t('employee.home.presenceType.hybrid') || 'مختلط'}</option>
                <option value="onsite">{t('employee.home.presenceType.onsite') || 'على موقع'}</option>
              </select>
            </div>

            <div className="d-flex gap-2">
              <div className="edit-profile-field-container flex-grow-1">
                <label className="edit-profile-label" htmlFor="experience">{t('company.createVacancy.experience') || 'سنوات الخبرة'}</label>
                <input
                  type="number"
                  id="experience"
                  min="0"
                  className="edit-profile-input"
                  {...register('experience')}
                />
              </div>

              <div className="edit-profile-field-container flex-grow-1">
                <label className="edit-profile-label" htmlFor="academic_degree">{t('company.createVacancy.academicDegree') || 'الدرجة العلمية'}</label>
                <input
                  type="text"
                  id="academic_degree"
                  className="edit-profile-input"
                  placeholder={t('company.createVacancy.academicDegreePlaceholder') || 'مثال: بكالوريوس'}
                  {...register('academic_degree')}
                />
              </div>
            </div>
          </div>

          {/* Salary & Location */}
          <div className="form-section-apply mb-4">
            <h3 className="form-section-title">{t('company.createVacancy.salaryLocation') || 'الراتب والموقع'}</h3>

            <div className="d-flex gap-2">
              <div className="edit-profile-field-container flex-grow-1">
                <label className="edit-profile-label" htmlFor="salary">{t('company.createVacancy.salary') || 'الراتب'}</label>
                <input
                  type="number"
                  id="salary"
                  step="0.01"
                  min="0"
                  className="edit-profile-input"
                  {...register('salary')}
                />
              </div>

              <div className="edit-profile-field-container flex-grow-1">
                <label className="edit-profile-label" htmlFor="currency">{t('company.createVacancy.currency') || 'العملة'}</label>
                <select id="currency" className="edit-profile-input" {...register('currency')}>
                  <option value="USD">{t('company.createVacancy.USD') || 'USD'}</option>
                  <option value="EUR">{t('company.createVacancy.EUR') || 'EUR'}</option>
                  <option value="EGP">{t('company.createVacancy.EGP') || 'EGP'}</option>
                </select>
              </div>
            </div>

            <div className="d-flex gap-2">
              <div className="edit-profile-field-container flex-grow-1">
                <label className="edit-profile-label" htmlFor="location_lat">{t('company.createVacancy.latitude') || 'خط العرض'}</label>
                <input
                  type="number"
                  id="location_lat"
                  step="0.000001"
                  className="edit-profile-input"
                  {...register('location_lat')}
                />
              </div>

              <div className="edit-profile-field-container flex-grow-1">
                <label className="edit-profile-label" htmlFor="location_long">{t('company.createVacancy.longitude') || 'خط الطول'}</label>
                <input
                  type="number"
                  id="location_long"
                  step="0.000001"
                  className="edit-profile-input"
                  {...register('location_long')}
                />
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="form-section-apply mb-4">
            <h3 className="form-section-title">{t('company.createVacancy.details') || 'التفاصيل'}</h3>

            <div className="edit-profile-field-container">
              <label className="edit-profile-label" htmlFor="description">{t('company.createVacancy.description') || 'الوصف'}</label>
              <textarea
                id="description"
                rows="6"
                className="notes-textarea"
                placeholder={t('company.createVacancy.descriptionPlaceholder') || 'وصف الوظيفة...'}
                {...register('description')}
              />
            </div>

            <div className="edit-profile-field-container">
              <label className="edit-profile-label" htmlFor="privileges">{t('company.createVacancy.privileges') || 'المميزات'}</label>
              <textarea
                id="privileges"
                rows="4"
                className="notes-textarea"
                placeholder={t('company.createVacancy.privilegesPlaceholder') || 'مميزات الوظيفة...'}
                {...register('privileges')}
              />
            </div>

            <div className="edit-profile-field-container">
              <label className="edit-profile-label" htmlFor="status">{t('company.createVacancy.status') || 'الحالة'}</label>
              <select id="status" className="edit-profile-input" {...register('status')}>
                <option value="open">{t('company.createVacancy.open') || 'مفتوح'}</option>
                <option value="closed">{t('company.createVacancy.closed') || 'مغلق'}</option>
              </select>
            </div>
          </div>

          {/* Skills Section */}
          <div className="form-section-apply mb-4">
            <h3 className="form-section-title">{t('company.createVacancy.skills') || 'المهارات'}</h3>
            <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>{t('company.createVacancy.skillsDescription') || 'اختر المهارات المطلوبة للوظيفة'}</p>
            <div className="d-flex flex-wrap gap-2">
              {skills.map((skill) => (
                <button
                  key={skill.id}
                  type="button"
                  onClick={() => handleSkillToggle(skill.id)}
                  className={`btn ${selectedSkills.includes(skill.id) ? 'btn-apply' : 'btn-view-vacancy'}`}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '0.5rem',
                    fontSize: '0.9rem',
                    border: selectedSkills.includes(skill.id) ? 'none' : '1px solid #ddd',
                    background: selectedSkills.includes(skill.id)
                      ? 'linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%)'
                      : 'white',
                    color: selectedSkills.includes(skill.id) ? 'white' : '#333',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {skill.name}
                </button>
              ))}
            </div>
            {skills.length === 0 && (
              <p className="text-muted" style={{ fontSize: '0.85rem' }}>{t('company.createVacancy.noSkillsAvailable') || 'لا توجد مهارات متاحة'}</p>
            )}
          </div>

          {/* Form Actions */}
          <div className="d-flex gap-2">
            <button
              type="button"
              onClick={() => navigate('/recruitment-company/vacancies')}
              className="btn-view-vacancy"
              style={{ flex: 1 }}
            >
              {t('company.createVacancy.cancel') || 'إلغاء'}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="btn-apply"
              style={{
                flex: 1,
                background: 'linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%)',
                color: 'white',
                padding: '0.875rem 1.5rem',
                borderRadius: '0.75rem',
                fontSize: '1rem',
                fontWeight: '600',
                border: 'none'
              }}
            >
              {submitting ? (t('company.createVacancy.creating') || 'جارٍ الإنشاء...') : (t('company.createVacancy.create') || 'إنشاء')}
            </button>
          </div>
        </form>
      </div>

      <RecruitmentCompanyBottomNavigation />
    </div>
  );
};

export default RecruitmentCompanyCreateVacancy;

